import { useEffect, useState } from 'react'
import type { DashboardCoaching } from '@/core/types'
import { currentVariant } from '@/core/data/session'
import { cardIncompleteCovers, descriptionFor } from '@/variants'

const NONE: ReadonlySet<string> = new Set()

export function useIncompleteCards(
  coachings: readonly DashboardCoaching[],
  version: number,
  enabled: boolean,
  hiddenStages: readonly string[],
): ReadonlySet<string> {
  const rules = enabled ? descriptionFor(currentVariant()).cardIncomplete : undefined
  const cardsKey = coachings
    .flatMap((c) => {
      const at = (rules ?? []).findIndex(
        (rule) => rule.isIncomplete && !hiddenStages.includes(rule.stage) && cardIncompleteCovers(rule, c),
      )
      return at < 0 ? [] : [`${at}:${c.id}`]
    })
    .join(',')
  const [incomplete, setIncomplete] = useState<ReadonlySet<string>>(NONE)

  useEffect(() => {
    const cards = (cardsKey ? cardsKey.split(',') : []).map((key) => {
      const at = key.indexOf(':')
      return { rule: Number(key.slice(0, at)), id: key.slice(at + 1) }
    })
    const current = new Set(cards.map((card) => card.id))
    setIncomplete((prev) => {
      const kept = new Set([...prev].filter((id) => current.has(id)))
      return kept.size === prev.size ? prev : kept
    })
    const probes = enabled ? (descriptionFor(currentVariant()).cardIncomplete ?? []) : []
    let cancelled = false
    const settle = (id: string, missing: boolean) =>
      setIncomplete((prev) => {
        if (prev.has(id) === missing) return prev
        const next = new Set(prev)
        if (missing) next.add(id)
        else next.delete(id)
        return next
      })
    for (const { rule, id } of cards) {
      const isIncomplete = probes[rule]?.isIncomplete
      if (!isIncomplete) continue
      isIncomplete(id)
        .then((missing) => {
          if (!cancelled) settle(id, missing)
        })
        .catch((e) => {
          console.error('Karten-Status laden fehlgeschlagen:', e)
          if (!cancelled) settle(id, false)
        })
    }
    return () => {
      cancelled = true
    }
  }, [enabled, cardsKey, version])

  return incomplete
}
